import { find } from 'lodash-es';

import {
  httpGetSharesCustomActionsByContextByActionIdViews,
  httpGetViewsByViewId,
  httpPatchViewsByViewId,
  type MetadataMetadataViewFieldSchema,
} from '@iconik-io/iconik-api/src/services/metadata';
import { replaceEntities } from '@iconik-io/shared-lib/src/redux/entities/slice';

import { FIELDS } from '../../constants/metadata';
import type { AppDispatch, GetState } from '../../store/types';
import { updateMetadataCategoryViews } from '../pages/admin_metadata_categories/thunks';
import type { CategoryObjectType } from '../pages/admin_metadata_categories/types';

export function updateMetadataView(
  viewId: string,
  fieldsInView: MetadataMetadataViewFieldSchema[],
) {
  return (dispatch: AppDispatch) =>
    dispatch(
      httpPatchViewsByViewId(viewId, { view_fields: fieldsInView }),
    ).then((response) => {
      // The patch answers with the saved view, whose fields describe each one
      // rather than just naming it, which is what the store holds.
      dispatch(
        replaceEntities({
          metadata_unmerged_views: {
            [viewId]: { ...response.body, id: viewId },
          },
        }),
      );
      return response;
    });
}

export function getMetadataView(viewId: string) {
  return (dispatch: AppDispatch) => dispatch(httpGetViewsByViewId(viewId));
}

export function getShareCustomActionMetadataView(
  context: string,
  actionId: string,
) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const shareAuth = getState().share.shareAuthLogin;
    const shareId = shareAuth?.share_id;
    const shareUserId = shareAuth?.share_user_id;
    if (!shareId || !shareUserId) {
      return Promise.reject(
        new Error(
          'Missing share auth (share_id or share_user_id). Aborting metadata view fetch.',
        ),
      );
    }
    return dispatch(
      httpGetSharesCustomActionsByContextByActionIdViews(context, actionId, {
        'Share-ID': shareId,
        'Share-User-ID': shareUserId,
      }),
    );
  };
}

export function getMetadataViewUnmerged(viewId: string) {
  return (dispatch: AppDispatch) =>
    dispatch(httpGetViewsByViewId(viewId, { merge_fields: false }));
}

export function getMetadataViewAndUnmergedFields(viewId: string) {
  return (dispatch: AppDispatch) =>
    Promise.all([
      dispatch(getMetadataViewUnmerged(viewId)),
      dispatch(getMetadataView(viewId)),
    ]);
}

export function addFieldToView(viewId: string, name: string, index?: number) {
  return (dispatch: AppDispatch, getState: GetState): Promise<unknown> => {
    // the updated should use the unmerged fields
    const unmergedView = getState().entities.metadata_unmerged_views[viewId];
    const fieldsInView: MetadataMetadataViewFieldSchema[] = [
      ...unmergedView.view_fields,
    ];
    const field = {
      name,
      auto_set: null,
      hide_if_not_set: null,
      label: null,
      options: [],
      read_only: null,
      required: null,
      source_url: null,
    };
    if (index !== undefined) {
      fieldsInView.splice(index, 0, field);
    } else {
      fieldsInView.push(field);
    }
    return dispatch(updateMetadataView(viewId, fieldsInView));
  };
}

export function removeFieldFromView(viewId: string, index?: number) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const state = getState();
    const unmergedMetadataView = state.entities.metadata_unmerged_views[viewId];
    // update both merged and unmerged fields
    const fieldsInView: MetadataMetadataViewFieldSchema[] =
      unmergedMetadataView.view_fields.filter((_field, i) => i !== index);
    const view = { ...unmergedMetadataView, view_fields: fieldsInView };
    return dispatch(updateMetadataView(view.id, view.view_fields));
  };
}

export function updateFieldsInView(
  viewId: string,
  fields: MetadataMetadataViewFieldSchema[],
) {
  return (dispatch: AppDispatch, getState: GetState) => {
    // the fields to send with the order should be the unmerged fields
    const unmergedFields =
      getState().entities.metadata_unmerged_views[viewId].view_fields;
    const update: MetadataMetadataViewFieldSchema[] = [];
    fields.forEach((f) => {
      if (f.name === FIELDS.SEPARATOR) {
        update.push({ ...f });
      } else {
        const field = find(unmergedFields, { name: f.name });
        if (field) {
          update.push({ ...field });
        }
      }
    });

    const metadataView = getState().entities.metadata_views[viewId];
    return dispatch(updateMetadataView(metadataView.id, update));
  };
}

export function updateFieldAttributesInView(
  viewId: string,
  field: MetadataMetadataViewFieldSchema,
) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const unmergedView = getState().entities.metadata_unmerged_views[viewId];
    const fields = unmergedView.view_fields.map((f) =>
      f.name === field.name ? field : f,
    );
    return dispatch(updateMetadataView(viewId, fields));
  };
}

export function addViewToCategory(
  viewId: string,
  objectType: CategoryObjectType,
  categoryName: string,
) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const category = find(
      getState().metadataCategories.metadataTypeCategories[objectType],
      { name: categoryName },
    );
    const label = category?.label ?? '';
    const viewIds = category?.view_ids ?? [];
    const ids = [...viewIds, viewId];
    return dispatch(
      updateMetadataCategoryViews(objectType, categoryName, label, ids),
    );
  };
}

export function removeViewFromCategory(
  viewId: string,
  objectType: CategoryObjectType,
  categoryName: string,
) {
  return (dispatch: AppDispatch, getState: GetState) => {
    const category = find(
      getState().metadataCategories.metadataTypeCategories[objectType],
      { name: categoryName },
    );
    const label = category?.label ?? '';
    const viewIds = category?.view_ids ?? [];
    const ids = viewIds.filter((id) => id !== viewId);
    return dispatch(
      updateMetadataCategoryViews(objectType, categoryName, label, ids),
    );
  };
}
